import { createFileRoute } from "@tanstack/react-router";
import { buildSeo } from "@/lib/seo";
import { DocsPageShell, type DocBlock } from "@/components/marketing/DocsLayout";

const blocks: DocBlock[] = [
  {
    type: "p",
    text: "The knowledge base is what makes a twin a twin instead of a generic chatbot. Every answer is grounded in your own content and cited back to a source — if the twin can't find a source, it says so rather than inventing one.",
  },
  { type: "h2", text: "Sources you can connect" },
  {
    type: "ul",
    items: [
      "Your website — crawled automatically across multiple pages when you onboard.",
      "Uploaded documents — PDF, DOCX, Markdown and plain text.",
      "Structured Q&A you add directly on the Knowledge page.",
    ],
  },
  { type: "h2", text: "How ingestion works" },
  {
    type: "ol",
    items: [
      "Pages and files are split into sentence-aware chunks so context isn't cut mid-thought.",
      "Each chunk is embedded (google/gemini-embedding-001, 768 dimensions) and stored with pgvector.",
      "At query time the visitor's message is embedded and the closest chunks are retrieved (RAG).",
      "The twin answers from those chunks and cites them inline as [1], [2], …",
    ],
  },
  {
    type: "callout",
    title: "Grounding is the rule",
    text: "If retrieval returns nothing relevant, the twin is instructed to be honest about what it doesn't know and offer a human — never to guess prices, commitments or facts.",
  },
  { type: "h2", text: "Re-crawling & freshness" },
  {
    type: "p",
    text: "Your website is re-crawled weekly by a scheduled job so the twin stays current. You can also trigger a manual re-crawl any time from the Knowledge page.",
  },
  { type: "h2", text: "Knowledge gaps" },
  {
    type: "p",
    text: "When a visitor asks a real question the knowledge base can't answer, Reality Twin records it (deduplicated and counted) so you see exactly what to add next. Closing these gaps is the fastest way to improve accuracy — and it doubles as content research for your site.",
  },
  { type: "h2", text: "Twin readiness score" },
  {
    type: "p",
    text: "Each twin has an AI readiness score reflecting how much it has learned — pages indexed, documents, and knowledge size. Higher scores mean more grounded, confident answers.",
  },
];

export const Route = createFileRoute("/docs/knowledge-base")({
  head: () =>
    buildSeo({
      path: "/docs/knowledge-base",
      title: "Knowledge Base — Reality Twin Docs",
      description:
        "Crawl your website, upload documents, and how RAG grounding with citations works.",
    }),
  component: () => (
    <DocsPageShell
      slug="knowledge-base"
      title="Knowledge base"
      description="Crawl your site, upload documents, and how grounding with citations works."
      blocks={blocks}
    />
  ),
});
